<template>
    <div ref="target" :class="{ 'has-back-button': hasBackButton }">
        <div v-show="currentState === 'initial'">
            <div class="search-form">
                <label for="route-search" aria-hidden="true">
                    {{ showAddress ? t('search_all') : t('search_route') }}
                </label>
                <div class="autocomplete-wrapper">
                    <AutocompleteInput
                        ref="routeSearch"
                        id="route-search"
                        v-model="search"
                        class="autocomplete-input"
                        :class="{ 'has-real-time-button': search === '' && showAddress }"
                        :list="routes"
                        :options="options"
                        :placeholder="showAddress ? t('address_placeholder') : t('widget_placeholder')"
                        :stops="showStops ? stops : []"
                        :addresses="showAddress ? addresses : []"
                        :date="date"
                        :loading="loading"
                        type="text"
                        @input="onSearchInput"
                    >
                        <template #route="{ result }">
                            <ServiceTypeItem
                                tag="div"
                                :route="result.item"
                                :service-type="result.item.codeTypeService"
                                :service-type-infos="getServiceType(result.item.codeTypeService)"
                                :route-name="routeName"
                                :date="date"
                            ></ServiceTypeItem>
                        </template>
                        <template #stop="{ result }">
                            <RouterLink
                                class="stop-result-link"
                                :to="{
                                    name: 'stop-single',
                                    params: {
                                        stop: slugify(`${result.entity_id}-${result.entity_title}`),
                                        date,
                                    },
                                }"
                            >
                                <div>
                                    <p class="results-item-name">
                                        {{ result.entity_id }} -
                                        <span v-html="result.entity_title"></span>
                                    </p>
                                    <small class="results-item-desc" v-html="result.description_stop"></small>
                                </div>
                                <div v-if="result.accesssibility === '1'">
                                    <span class="visuallyhidden">{{ $t('a11y.accessible.accessible_stop') }}</span>
                                    <img src="@@/img/accessible.svg" width="18" height="18" />
                                </div>
                                <span v-else class="visuallyhidden">
                                    {{ $t('a11y.accessible.not_accessible_stop') }}
                                </span>
                            </RouterLink>
                        </template>
                        <template #address="{ result }">
                            <button
                                type="button"
                                class="address-result-button"
                                @click="
                                    changeState(
                                        'addressResults',
                                        null,
                                        `${result.address.coord.lat},${result.address.coord.lon}`,
                                        `${result.address.house_number !== 0 ? result.address.house_number : ''} ${
                                            result.address.name
                                        }`,
                                    )"
                            >
                                <p class="results-item-name">
                                    <template v-if="result.address.house_number !== 0">
                                        {{ result.address.house_number }}
                                    </template>
                                    {{ result.address.name }}
                                </p>
                                <small class="results-item-desc">
                                    {{ result.address.administrative_regions[0].name }}
                                </small>
                            </button>
                        </template>
                        <template v-if="showAddress" #button>
                            <div class="input-buttons">
                                <button v-if="search !== ''" class="close-button" type="button" @click="clearSearch">
                                    <span class="visuallyhidden">{{ $t('a11y.search.erase') }}</span>
                                    <IconClose width="12" height="12" />
                                </button>
                                <button
                                    v-else
                                    class="search-button button"
                                    :class="{ 'is-error': geolocationError }"
                                    type="button"
                                    @click="getUserPosition"
                                >
                                    <span :aria-label="t('ntr_nearby')">{{ t('nearby') }}</span>
                                    <span v-if="isGeolocating" class="loader"></span>
                                    <IconRealtime v-else width="10" height="10" />
                                </button>
                            </div>
                        </template>
                    </AutocompleteInput>
                </div>
            </div>
            <ul v-if="showServices" class="no-bullet service-type-list">
                <li v-for="(serviceType, i) in servicesTypes" :key="`service-${i}`" class="service-item">
                    <button
                        type="button"
                        class="service-type-button button"
                        :class="[serviceType.class, `service-type-button-${serviceType.code}`]"
                        @click="changeState('routeResults', serviceType.code)"
                    >
                        <img :src="serviceType.icon" width="32" height="32" aria-hidden="true" alt="" />
                        <span class="service-name" :class="serviceType.class">{{ serviceType.name }}</span>
                    </button>
                </li>
            </ul>
        </div>
        <template v-if="showServices">
            <div v-show="currentState === 'routeResults'" class="services-list-container">
                <div>
                    <button ref="returnSearch" type="button" class="go-back-button" @click="changeState('initial')">
                        <IconDropdownArrow class="back-icon" width="8" height="4" />{{ t('return_search') }}
                    </button>
                    <button
                        v-show="chosenServiceType === 0 && filterRoute(2).length"
                        type="button"
                        class="school-route-button"
                        @click="changeState('school')"
                    >
                        {{ t('school_routes') }}
                        <IconDropdownArrow class="school-button-icon" width="6" height="12" />
                    </button>
                </div>
                <AccordionList individual class="service-types-list">
                    <ServiceTypeItem
                        v-for="item in currentRoutes"
                        :key="item.parcours"
                        :route="item"
                        :service-type="chosenServiceType"
                        :service-type-infos="currentService"
                        :route-name="routeName"
                        :date="date"
                    ></ServiceTypeItem>
                </AccordionList>
            </div>
            <div v-show="currentState === 'school'" class="services-list-container">
                <button ref="returnSchool" type="button" class="go-back-button" @click="changeState('routeResults', 0)">
                    <IconDropdownArrow class="back-icon" width="8" height="4" />{{ t('return_lebus') }}
                </button>
                <AccordionList individual class="service-types-list">
                    <ServiceTypeItem
                        v-for="item in filterRoute(2)"
                        :key="item.parcours"
                        :route="item"
                        :service-type="2"
                        :service-type-infos="getServiceType(2)"
                        :route-name="routeName"
                        :date="date"
                    ></ServiceTypeItem>
                </AccordionList>
            </div>
        </template>
        <div v-show="currentState === 'addressResults'" class="address-results services-list-container">
            <button ref="returnSearchAddress" type="button" class="go-back-button" @click="changeState('initial')">
                <IconDropdownArrow class="back-icon" width="8" height="4" />{{ t('return_search') }}
            </button>
            <p v-if="selectedAddress" class="results-msg">
                {{ t('stops_routes_near_address') }} « {{ selectedAddress }} »
            </p>
            <p v-if="userPosition" class="results-msg">{{ t('stops_routes_near_user') }}</p>
            <DotsLoading v-if="stopsLoading" />
            <template v-else>
                <ul v-if="nearbyStops.length > 0" class="no-bullet service-types-list">
                    <li v-for="(stop, i) in nearbyStops" :key="`stop-${i}-${stop.noArret}`">
                        <div class="stop-header">
                            <strong>{{ stop.noArret }}</strong>
                            - <span v-html="stop.nom"></span>
                        </div>
                        <ul class="no-bullet">
                            <li v-for="(item, j) in stop.parcours" :key="`route-stop-${j}`">
                                <RouteButton
                                    :route-params="{
                                        route: item.parcours.noParcours,
                                        stop: `${stop.noArret}-${slugify(stop.nom)}`,
                                        direction: directionSlug(item.parcours.codeDirection, item.parcours.descriptionDirection),
                                    }"
                                    :route="item.parcours"
                                    :station="item"
                                    :index="j + 1"
                                    :length="stop.parcours.length"
                                    :direction="item.parcours.descriptionDirection"
                                    :ntr-enabled="ntrEnabled"
                                    @refresh="startLoop"
                                />
                            </li>
                        </ul>
                    </li>
                </ul>
                <div v-else class="no-results-msg">{{ t('no_stops_routes_near_user') }}</div>
            </template>
        </div>
        <LiveRegion ref="liveRegion" />
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, reactive, provide } from 'vue';
import { onClickOutside, useFocusWithin } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { storeToRefs } from 'pinia';

import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import { useSchedulesStore } from '@@/js/stores/schedules';
import directionSlug from '@@/js/utils/directionSlug';
import navitia from '@@/js/api/navitia';
import slugify from '@@/js/utils/slugify';
import AccordionList from '@/shared/AccordionList';
import AutocompleteInput from '@/shared/AutocompleteInput';
import DotsLoading from '@/shared/DotsLoading';
import LiveRegion from '@/shared/LiveRegion';
import RouteButton from '@/home-route-selector/components/RouteButton';
import ServiceTypeItem from '@/shared/ServiceTypeItem';

import IconClose from '@@/icons/close.svg';
import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';
import IconRealtime from '@@/icons/realtime.svg';

const props = defineProps({
    routeName: {
        type: String,
        default: 'printable-schedule',
    },
    date: {
        type: String,
        default: undefined,
    },
    triggerRef: {
        type: Object,
        default: null,
    },
    showStops: {
        type: Boolean,
        default: true,
    },
    showAddress: {
        type: Boolean,
        default: false,
    },
    showServices: {
        type: Boolean,
        default: true,
    }
});

const emit = defineEmits(['close', 'change-state']);

defineExpose({ routeSearch, focus });

const routeSearch = ref();

function focus() {
    if (routeSearch.value && routeSearch.value.focus) {
        routeSearch.value.focus();
    }
}

const { t } = useI18n({
    messages: {
        fr: {
            search_route: 'Chercher un parcours ou arrêt',
            search_all: 'Rechercher par parcours, arrêt ou adresse',
            widget_placeholder: 'No de parcours ou d’arrêt',
            address_placeholder: 'Parcours, arrêt ou adresse',
            return_search: 'Retour à la recherche',
            return_lebus: 'Retour aux parcours',
            school_routes: 'Voir les parcours écoles',
            nearby: 'À proximité',
            ntr_nearby: 'Temps réel à proximité',
            have_been_located: 'Vous avez bien été localisé',
            geoloc_error_1: 'La localisation a été désactivée pour ce site.',
            geoloc_error_2: 'Impossible de récupérer votre position actuellement.',
            geoloc_error_3: 'Le délai de récupération a été dépassé.',
            stops_routes_near_address: 'Arrêts et parcours à proximité de',
            stops_routes_near_user: 'Arrêts et parcours à proximité de ma position',
            no_stops_routes_near_user: 'Aucun arrêt ni parcours à proximité',
        },
        en: {
            search_route: 'Search for a route or stop',
            search_all: 'Search for route, stop or address',
            widget_placeholder: 'Route or stop number',
            address_placeholder: 'Route, stop or address',
            return_search: 'Return to search',
            return_lebus: 'Return to routes list',
            school_routes: 'See school routes',
            nearby: 'Nearby',
            ntr_nearby: 'Real time nearby',
            have_been_located: 'You have been successfully located',
            geoloc_error_1: 'Geolocation is disabled on this website.',
            geoloc_error_2: 'Unable to retrieve your location.',
            geoloc_error_3: 'Timeout delay was exceeded.',
            stops_routes_near_address: 'Stops and routes near',
            stops_routes_near_user: 'Stops and routes near my position',
            no_stops_routes_near_user: 'No stops or routes nearby',
        },
    },
});

const target = ref();
onClickOutside(target, (event) => {
    if (props.triggerRef && (event.target === props.triggerRef || props.triggerRef.contains(event.target))) {
        return;
    }
    emit('close');
});

const { focused } = useFocusWithin(target);

watch(focused, (isFocused) => {
    if (!isFocused) {
        setTimeout(() => {
            if (focused.value) return;
            if (props.triggerRef && document.activeElement === props.triggerRef) {
                return;
            }
            emit('close');
        }, 0);
    }
});

const search = ref('');
const options = ref({ keys: ['noParcours'], threshold: 0.1 });
const currentState = ref('initial');
const chosenServiceType = ref(0);
const currentRoutes = computed(() => filterRoute(chosenServiceType.value));
const currentService = computed(() => getServiceType(chosenServiceType.value));

const stops = ref([]);
const loading = ref(false);
const lastStopSearch = ref();

const schedulesStore = useSchedulesStore();
const { fetchAutocompleteStops, fetchNearbyStops, fetchStopsDepartures, getServiceType, sortStations } = schedulesStore;
const { routes, routesByServices } = storeToRefs(schedulesStore);

async function updateAutocompleteStops(search) {
    stops.value = [];
    let newStops = [];
    const stopSearchTime = new Date();
    lastStopSearch.value = stopSearchTime;
    loading.value = true;
    try {
        newStops = await fetchAutocompleteStops(search);
        loading.value = false;
    } catch (e) {}
    if (lastStopSearch.value === stopSearchTime) {
        stops.value = newStops;
        stops.value = stops.value.filter((stop) => {
            return stop.entity_id !== '';
        });
        lastStopSearch.value = null;
    }
}

const servicesTypes = computed(() => {
    return routesByServices.value.filter((s) => typeof s.showWith === 'undefined');
});

watch(search, (newSearch) => {
    updateAutocompleteStops(newSearch);
});

const returnSchool = ref();
const returnSearch = ref();
const returnSearchAddress = ref();
const selectedAddress = ref('');
const chosenAddressLatLng = ref('');

async function changeState(state, serviceType = null, addressLatLng = '', address = '') {
    currentState.value = state;
    if (props.showAddress) {
        chosenAddressLatLng.value = addressLatLng;
        selectedAddress.value = address;
        geolocationError.value = '';
        stopLoop();
    }
    window.addEventListener('keyup', handleKeyDown);
    switch (currentState.value) {
        case 'initial':
            userPosition.value = null;
            search.value = '';
            window.removeEventListener('keyup', handleKeyDown);
            await nextTick();
            if (chosenServiceType.value !== null && props.showServices) {
                target.value.querySelector(`.service-type-button-${chosenServiceType.value}`).focus();
            }
            if (!props.showServices) {
                focus();
            }
            break;
        case 'school':
            await nextTick();
            returnSchool.value.focus();
            break;
        case 'routeResults':
            chosenServiceType.value = serviceType;
            await nextTick();
            returnSearch.value.focus();
            break;
        case 'addressResults':
            await nextTick();
            returnSearchAddress.value.focus();
            startLoop();
            break;
    }
    emit('change-state', currentState.value);
}

function handleKeyDown(event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
        if (currentState.value !== 'school') {
            changeState('initial');
        } else {
            changeState('routeResults', 0);
        }
    }
}

function filterRoute(serviceTypeCode) {
    const serviceType = routesByServices.value.find((s) => s.code === serviceTypeCode);
    return serviceType?.routes || [];
}

const hasBackButton = computed(() => currentState.value === 'routeResults' || currentState.value === 'school');

const headers = ref([]);

function registerHeader(headerEl) {
    if (headerEl && !headers.value.includes(headerEl)) {
        headers.value.push(headerEl);
    }
}

function unregisterHeader(headerEl) {
    headers.value = headers.value.filter((el) => el !== headerEl);
}

const accordionListState = reactive({
    registerHeader,
    unregisterHeader,
    headers,
    individual: false,
    disabled: false,
});

const addresses = ref([]);
async function updateAddresses(search) {
    if (search !== '' && search.length >= 3) {
        try {
            addresses.value = await navitia.getPlaces(search);
        } catch (error) {
            addresses.value = [];
        }
    } else {
        addresses.value = [];
    }
}

function onSearchInput(event) {
    const value = event.target.value;
    updateAddresses(value);
}

function clearSearch() {
    search.value = '';
}

const liveRegion = ref();
const isGeolocating = ref(false);
const geolocationError = ref('');
const userPosition = ref(null);
const stopsLoading = ref(false);
const nextDeparturesLoop = ref(null);
const timeout = ref();

async function startLoop() {
    const clearDelay = 2 * 60 * 60 * 1000; // 2 heures
    const refreshRate = 30 * 1000; // 30 secondes
    stopLoop();
    timeout.value = setTimeout(stopLoop, clearDelay);
    stopsLoading.value = true;
    await updateNearbyStops();
    stopsLoading.value = false;
    nextDeparturesLoop.value = setInterval(updateNearbyStops, refreshRate);
}

function stopLoop() {
    nextDeparturesLoop.value && clearInterval(nextDeparturesLoop.value);
    timeout.value && clearTimeout(timeout.value);
}

function getUserPosition() {
    search.value = '';
    isGeolocating.value = true;
    geolocationError.value = '';
    const position = new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 20000,
        });

        function PositionError(code, message) {
            // this.code = code;
            // this.message = message;
        }
        PositionError.PERMISSION_DENIED = 1;
        PositionError.POSITION_UNAVAILABLE = 2;
        PositionError.TIMEOUT = 3;
        // eslint-disable-next-line unicorn/error-message
        PositionError.prototype = new Error();

        window.setTimeout(() => {
            reject(new PositionError(PositionError.TIMEOUT, 'Timed out'));
        }, 20000);
    });
    position
        .then((position) => {
            userPosition.value = position;
            changeState('addressResults', null, `${position.coords.latitude},${position.coords.longitude}`);
            liveRegion.value.setMessage(t('have_been_located'));
        })
        .catch((error) => {
            geolocationError.value = t(`geoloc_error_${error.code}`);
        })
        .finally(() => {
            isGeolocating.value = false;
        });
}

const { getEffectiveDate } = useEffectiveDate();
const nearbyStops = ref([]);

async function updateNearbyStops() {
    const stopsList = [];
    const date = getEffectiveDate(new Date(), 'yyyy-MM-dd');
    const fetchedNearbyStops = await fetchNearbyStops(chosenAddressLatLng.value);
    if (fetchedNearbyStops && fetchedNearbyStops.length > 0) {
        const nearbyStopList = fetchedNearbyStops.reduce((acc, arret) => {
            acc.push(arret.entity_id);
            return acc;
        }, []);
        let departures = await fetchStopsDepartures(nearbyStopList, date);
        if (departures && departures.length > 0) {
            departures = departures.filter((p) => {
                return p.typeParcours === 'PP';
            });
            departures.forEach((departure) => {
                const inArray = stopsList.find((i) => i.noArret === departure.arret.noArret);
                const stop = inArray || { parcours: [] };
                if (!inArray) {
                    Object.entries(departure.arret).forEach(([key, value]) => {
                        stop[key] = value;
                    });
                    stopsList.push(stop);
                }
                stop.parcours.push({
                    ...departure,
                    parcours: {
                        ...departure.parcours,
                        serviceType: getServiceType(departure.parcours.codeTypeService),
                    },
                });
            }, []);
        }
        stopsList.forEach((stop) => sortStations(stop.parcours));
    }
    nearbyStops.value = stopsList;
}

const ntrEnabled = computed(() => nextDeparturesLoop.value !== null ? !!nextDeparturesLoop.value : nextDeparturesLoop.value);

provide('accordionListState', accordionListState);
</script>

<style lang="scss" scoped>
.route-search-container.has-back-button {
    padding: 0;
}
.go-back-button,
.school-route-button {
    @include accessible-focus;
    @include rem-typo(14px);
    width: 100%;
    text-align: left;
    color: $color-blue-60;
    border-bottom: 1px solid $color-black-10;
    flex-shrink: 0;
}
.go-back-button {
    padding: 12px 16px;
    background-color: $color-white;
    display: flex;
    align-items: center;
    gap: 8px;
    border: 0;
    border-radius: 20px 20px 0 0;
}
.back-icon {
    transform: rotate(90deg);
}
.school-route-button {
    padding: 16px;
    background-color: $color-black-2;
    border: 0;
}
.school-button-icon {
    margin-left: 6px;
    transform: rotate(-90deg);
}
.service-types-list,
.autocomplete-input {
    :deep(.accordion-button) {
        @include accessible-focus;
        border-radius: 0;
        position: relative;
    }
}
.service-types-list {
    position: relative;
    overflow-y: auto;
    flex-grow: 1;
    border-radius: 0 0 15px 15px;
    @include breakpoint(medium) {
        border-radius: 0 0 20px 20px;
    }
}
.search-form {
    margin-bottom: 16px;
    label {
        @include rem-typo(14px);
        display: block;
        font-weight: 800;
        margin-bottom: 12px;
        @include breakpoint(medium) {
            @include rem-typo(16px);
        }
    }
}
.autocomplete-wrapper {
    display: flex;
    align-items: flex-start;
}
.autocomplete-input {
    flex-grow: 1;
    &.has-real-time-button {
        :deep(.autocomplete-input) {
            padding-right: 140px;
            height: 50px;
            @media (max-width: 375px) {
                &::placeholder {
                    color: $color-black-0;
                }
            }
        }
    }
}
.stop-result-link {
    @include accessible-focus;
    width: 100%;
    padding: 8px 16px;
    text-align: left;
    text-decoration: none;
    color: $color-black;
    display: flex;
    justify-content: space-between;
}
.results-item-name {
    @include rem-typo(18px);
    margin-bottom: -8px;
}
.results-item-desc {
    @include rem-typo(12px);
    color: $color-black-50;
}
.services-list-container {
    max-height: 575px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.service-name {
    @include rem-typo(12px);
    display: inline-flex;
    font-weight: 700;
    align-items: center;
    @include colorize {
        color: get-color-variation('base');
    }
    @include breakpoint(360px) {
        height: auto;
    }
}
.service-type-list {
    display: flex;
    justify-content: space-between;
}
.service-item {
    width: 24%;
    margin-right: 2px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    @include breakpoint(medium) {
        margin-right: 0;
    }
    &:last-child {
        margin-right: 0;
    }
}
.service-type-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 16px 0 24px;
    text-align: center;
    border-radius: 10px;
    border: 0;
    @include colorize {
        background-color: get-color-variation('lighter');
    }
    &:hover,
    &:focus-visible {
        @include colorize {
            background-color: get-color-variation('lightHover');
        }
    }
}
.results-item-name {
    @include rem-typo(18px);
    margin-bottom: -8px;
}
.results-item-desc {
    @include rem-typo(12px);
    color: $color-black-50;
}
.input-buttons {
    z-index: 15;
    position: absolute;
    top: 50%;
    right: 4px;
    display: flex;
    align-items: center;
    transform: translateY(-50%);
}
.close-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: transparent;
    border: 0;
    border-radius: 50%;
    padding: 0;
    svg {
        fill: $color-black-60;
    }
}
.address-result-button {
    @include accessible-focus;
    width: 100%;
    padding: 8px 16px;
    line-height: 1.5;
    text-align: left;
    background: transparent;
    border: 0;
}
.results-msg,
.no-results-msg {
    margin: 0;
    padding: 8px 16px;
    line-height: 1.4;
    color: $color-black-50;
    border-top: 1px solid $color-black-10;
    @include breakpoint(medium) {
        line-height: 1.6;
    }
}
.no-results-msg {
    color: $color-black;
    border-bottom: none;
}
.geolocation-error {
    @include rem-typo(14px);
    padding: 4px 0;
    color: $color-alert;
    @include breakpoint(medium) {
        position: absolute;
        bottom: 0;
        transform: translateY(100%);
    }
}
.loader {
    width: 16px;
    height: 16px;
    border: 2px solid $color-black-2;
    border-top: 2px solid transparent;
    border-radius: 50%;
    animation: spin 2s linear infinite;
}
@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
.address-results {
    margin: 0 -16px;
    height: 350px;
    @include breakpoint(medium) {
        margin: 0 -24px;
        height: 475px;
    }
}
.stop-header {
    background-color: $color-black-8;
    padding: 8px 16px;
}
</style>
